import { forwardRef, useCallback, useEffect, useState } from 'react';

import { HeaderAction } from '@/components/Header/Action/HeaderAction';
import { MenuToggleIcon } from '@/components/MenuToggleButton/MenuToggleIcon';
import { cx } from '@/utils/cx';

import { useCategoryDropdownContext } from './CategoryDropdownContext';
import styles from './HeaderCategoryTrigger.module.scss';

import type { VisibleOn } from '@/components/Header/types';

/**
 * Header 의 Top icon button 패턴 preset — CategoryDropdown 와 자동 연동.
 *
 * 자동 처리:
 * - `action="category"` (Header.Action 의 action 식별자)
 * - `variant="icon"` (label 없는 icon-only)
 * - PC 한정 36×36 사이즈 (widget 자체 SCSS module 책임)
 * - `icon={<MenuToggleIcon toggled={ctx.open} ...20/24 사이즈/>}` — toggled 가 context 의 open 자동 추적
 * - `onClick` → context 의 handleTriggerClick
 * - mount 시 자체 element 를 anchor 로 자동 등록 (단일 trigger 케이스에서 anchorEl prop 명시 불필요)
 * - `aria-label="카테고리"` (default)
 *
 * Opinionated default — 흔한 케이스의 shortcut:
 * - `visibleOn="desktop"` — 카테고리 트리거는 PC GNB 패턴
 * - `showOnScroll=false` — 펼쳐진 헤더부터 항상 노출. 스크롤 시 collapsed 후에만 노출하려면 `showOnScroll` 명시.
 *
 * 호스트가 default 와 다른 visibility 가 필요하면 prop 으로 override.
 * 더 자유로운 형태가 필요하면 `<CategoryDropdown.Trigger>` (generic enhance) 사용.
 *
 * Header.* dot-notation 의 *예외적* widget-backed component — Header 의 다른 dot-notation 은 모두 dumb.
 * CategoryDropdown context 부재 시 dev warn + click no-op.
 */
export interface HeaderCategoryTriggerProps {
  /** 호스트 추가 클릭 핸들러. context toggle 전에 실행. preventDefault 시 toggle skip */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** 노출 트래킹 콜백 (Header.Action 의 onImpressed pass-through) */
  onImpressed?: () => void;
  /** 디바이스별 노출. default `'desktop'` — 카테고리 트리거는 PC GNB 패턴 */
  visibleOn?: VisibleOn;
  /** 스크롤 시에만 표시. default `true` — 펼쳐진 헤더부터 항상 노출. */
  showOnScroll?: boolean;
  /** custom aria-label (default '카테고리') */
  'aria-label'?: string;
  /** custom className */
  className?: string;
  /** Header 의 onAction 콜백에 전달할 meta. action 식별자는 'category' 고정 */
  meta?: Record<string, unknown>;
}

export const HeaderCategoryTrigger = forwardRef<HTMLButtonElement, HeaderCategoryTriggerProps>(
  function HeaderCategoryTrigger(
    { onClick, onImpressed, visibleOn = 'desktop', showOnScroll = true, 'aria-label': ariaLabel, className, meta },
    forwardedRef,
  ) {
    const ctx = useCategoryDropdownContext();

    if (process.env.NODE_ENV !== 'production' && ctx === null) {
      // eslint-disable-next-line no-console
      console.warn('[core-ui] Header.CategoryTrigger requires <CategoryDropdown> ancestor. Click will no-op.');
    }

    const isOpen = ctx?.open ?? false;

    // ── 자동 등록 — 책임 분리 ─────────────────────────────────────
    // [책임 1] callback ref → element 를 state 로 저장 + caller 의 ref forward.
    // [책임 2] useEffect → state 변경 시 register / cleanup 시 unregister.
    //
    // callback ref 안에서 register/unregister 까지 다 하면 *3 책임이 한 함수* 라 복잡.
    // state + effect 로 분리하면 React 의 일반 데이터 흐름과 동일 — 읽기 쉬움.

    // [책임 1] element 를 state 로 저장 + caller forward
    const [el, setEl] = useState<HTMLButtonElement | null>(null);

    const ref = useCallback(
      (node: HTMLButtonElement | null) => {
        setEl(node);
        // forward to caller's ref (object ref 또는 callback ref 둘 다 지원)
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          (forwardedRef as { current: HTMLButtonElement | null }).current = node;
        }
      },
      [forwardedRef],
    );

    // [책임 2] element/ctx 변경 시 register, cleanup 시 unregister
    useEffect(() => {
      if (!el || !ctx) return;
      return ctx.registerTrigger(el);
    }, [el, ctx]);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(e);
      if (!e.defaultPrevented && ctx !== null) {
        ctx.handleTriggerClick();
      }
    };

    return (
      <HeaderAction
        action="category"
        aria-expanded={isOpen}
        aria-label={ariaLabel ?? '카테고리'}
        className={cx(styles.trigger, className)}
        icon={<MenuToggleIcon maxBarWidthPx={16} toggled={isOpen} touchTargetPx={24} />}
        meta={meta}
        onClick={handleClick}
        onImpressed={onImpressed}
        ref={ref}
        showOnScroll={showOnScroll}
        variant="icon"
        visibleOn={visibleOn}
      />
    );
  },
);

HeaderCategoryTrigger.displayName = 'Header.CategoryTrigger';
